import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Eye, Globe, Users, Shield, Target, AlertCircle, CheckCircle, Plus, ChevronLeft, ChevronRight, Download, Flame, Zap } from 'lucide-react';
import { MetricDetailsModal } from './MetricDetailsModal';
import { EnhancedMetricModal } from './EnhancedMetricModal';
import { DataTaggingComponent } from '@/components/DataManagement/DataTaggingComponent';
import { useToast } from '@/hooks/use-toast';
import { unifiedLibraryService } from '@/services/unifiedLibraryService';
import { UnifiedMetric, UserMetricData } from '@/types/unifiedLibrary';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PDFUtils } from '@/utils/pdfUtils';
import { useDownloadGate } from '@/components/DownloadGate/useDownloadGate';

export const MetricsLibrary = () => {
  const [metrics, setMetrics] = useState<UnifiedMetric[]>([]);
  const [userMetrics, setUserMetrics] = useState<UserMetricData[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [subcategories, setSubcategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedMetric, setSelectedMetric] = useState<UnifiedMetric | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const { toast } = useToast();
  const { guardDownload } = useDownloadGate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [metricsData, userMetricsData, categoriesData] = await Promise.all([
        unifiedLibraryService.getMetrics(),
        unifiedLibraryService.getUserMetrics(),
        unifiedLibraryService.getMetricCategories()
      ]);

      setMetrics(metricsData);
      setUserMetrics(userMetricsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading metrics data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load metrics library',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCategory !== 'all') {
      loadSubcategories(selectedCategory);
    } else {
      setSubcategories([]);
    }
  }, [selectedCategory]);

  const loadSubcategories = async (category: string) => {
    try {
      const subcategoriesData = await unifiedLibraryService.getMetricSubcategories(category);
      setSubcategories(subcategoriesData);
    } catch (error) {
      console.error('Error loading subcategories:', error);
    }
  };

  const difficulties = ['Easy', 'Medium', 'Hard', 'Expert'];

  const filteredMetrics = metrics.filter(metric => {
    const matchesSearch = metric.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         metric.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         metric.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || metric.category === selectedCategory;
    const matchesSubcategory = selectedSubcategory === 'all' || metric.subcategory === selectedSubcategory;
    const matchesDifficulty = selectedDifficulty === 'all' || metric.difficulty === selectedDifficulty;
    
    return matchesSearch && matchesCategory && matchesSubcategory && matchesDifficulty;
  });

  const totalPages = Math.ceil(filteredMetrics.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMetrics = filteredMetrics.slice(startIndex, endIndex);

  const handleViewDetails = (metric: UnifiedMetric) => {
    console.log('View button clicked for metric:', metric.name);
    setSelectedMetric(metric);
    setIsViewModalOpen(true);
    console.log('Modal state set to open');
  };

  const handleAddMetric = (metric: UnifiedMetric) => {
    console.log('Add button clicked for metric:', metric.name);
    setSelectedMetric(metric);
    setIsAddModalOpen(true);
    console.log('Add modal state set to open');
  };

  const handleDownloadPDF = async () => {
    if (!(await guardDownload())) return;

    try {
      const blob = await PDFUtils.createCompletePDF(
        {
          title: 'Sustainability Metrics Library',
          subtitle: 'Comprehensive Metrics Database',
          exportType: 'metrics_library',
          includeLogo: true,
          includeFooter: true,
          includeSummary: true,
          summaryData: {
            totalRecords: filteredMetrics.length,
            exportFormat: 'PDF',
            additionalInfo: [
              `Generated on: ${new Date().toLocaleDateString()}`,
              `Filtered metrics: ${filteredMetrics.length.toLocaleString()}`,
              `Categories: ${[...new Set(filteredMetrics.map(m => m.category))].length}`
            ]
          }
        },
        (pdf, yPosition, pageNum) => {
          // Add filtered metrics summary
          pdf.setFontSize(14);
          pdf.setFont(undefined, 'bold');
          pdf.text('Metrics Summary:', 20, yPosition);
          pdf.setFont(undefined, 'normal');
          
          yPosition += 15;
          pdf.setFontSize(10);
          
          const metricsPerPage = 20; // Reduced to account for header
          
          filteredMetrics.slice(0, metricsPerPage).forEach((metric, index) => {
            const { yPosition: newY, pageNum: newPageNum } = PDFUtils.handlePageBreak(pdf, yPosition, pageNum, 250);
            yPosition = newY;
            pageNum = newPageNum;
            
            pdf.text(`${index + 1}. ${metric.name}`, 20, yPosition);
            pdf.text(`Category: ${metric.category} | Unit: ${metric.unit}`, 25, yPosition + 8);
            pdf.text(`Difficulty: ${metric.difficulty || 'N/A'} | Mandatory: ${metric.mandatory ? 'Yes' : 'No'}`, 25, yPosition + 16);
            yPosition += 25;
          });
          
          return { yPosition, pageNum };
        }
      );
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `metrics-library-${new Date().toLocaleDateString()}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleDownloadCSV = async () => {
    if (!(await guardDownload())) return;

    const headers = ['Name', 'Category', 'Subcategory', 'Unit', 'Difficulty', 'Mandatory', 'Scope', 'Description'];
    const csvContent = [
      headers.join(','),
      ...filteredMetrics.map(metric => [
        `"${metric.name}"`,
        metric.category,
        metric.subcategory,
        metric.unit,
        metric.difficulty || 'N/A',
        metric.mandatory ? 'Yes' : 'No',
        metric.scope || 'N/A',
        `"${metric.description || ''}"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `metrics-library-${new Date().toLocaleDateString()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Environmental': return <Globe className="h-4 w-4" />;
      case 'Social': return <Users className="h-4 w-4" />;
      case 'Governance': return <Shield className="h-4 w-4" />;
      case 'Economic': return <Target className="h-4 w-4" />;
      case 'Material': return <Flame className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Hard': return 'bg-orange-100 text-orange-800';
      case 'Expert': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const hasEmissionFactor = (metric: UnifiedMetric) => {
    return metric.emission_factor !== null && metric.emission_factor !== undefined;
  };

  const hasCalorificValue = (metric: UnifiedMetric) => {
    return metric.calorific_value !== null && metric.calorific_value !== undefined;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Metrics Library</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading metrics library...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Sustainability Metrics Library</CardTitle>
          <div className="text-sm text-gray-600">
            Comprehensive database of {metrics.length} sustainability metrics with standards mapping
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search metrics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={selectedCategory} onValueChange={(value) => {
              setSelectedCategory(value);
              setSelectedSubcategory('all');
              setCurrentPage(1);
            }}>
              <SelectTrigger>
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories && categories.map(category => (
                  <SelectItem key={category} value={category}>{category}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedSubcategory} onValueChange={(value) => {
              setSelectedSubcategory(value);
              setCurrentPage(1);
            }}>
              <SelectTrigger>
                <SelectValue placeholder="Subcategory" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subcategories</SelectItem>
                {subcategories && subcategories.map(subcategory => (
                  <SelectItem key={subcategory} value={subcategory}>{subcategory}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedDifficulty} onValueChange={(value) => {
              setSelectedDifficulty(value);
              setCurrentPage(1);
            }}>
              <SelectTrigger>
                <SelectValue placeholder="Difficulty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Difficulties</SelectItem>
                {difficulties.map(difficulty => (
                  <SelectItem key={difficulty} value={difficulty}>{difficulty}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex gap-2">
              <Button variant="outline" onClick={handleDownloadPDF} size="sm">
                <Download className="h-4 w-4 mr-2" />
                PDF
              </Button>
              <Button variant="outline" onClick={handleDownloadCSV} size="sm">
                <Download className="h-4 w-4 mr-2" />
                CSV
              </Button>
            </div>
          </div>

          {/* Metrics Table */}
          <div className="rounded-md border overflow-hidden mb-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Difficulty</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Mandatory</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedMetrics.map((metric) => (
                  <TableRow key={metric.id}>
                    <TableCell className="font-medium">{metric.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(metric.category)}
                        {metric.category}
                      </div>
                    </TableCell>
                    <TableCell>{metric.unit}</TableCell>
                    <TableCell>
                      <Badge className={getDifficultyColor(metric.difficulty)}>
                        {metric.difficulty}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {metric.scope}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {metric.mandatory ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-gray-400" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(metric)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAddMetric(metric)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-4 border-t border-gray-200 gap-4">
              <div className="text-sm text-gray-700 text-center sm:text-left">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredMetrics.length)} of {filteredMetrics.length} results
              </div>
              <div className="flex gap-2 ml-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Metric Details Modal */}
      <MetricDetailsModal
        metric={selectedMetric}
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
      />

      {/* Simple Test Modal */}
      {isViewModalOpen && (
        <Dialog open={isViewModalOpen} onOpenChange={() => setIsViewModalOpen(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Test Modal</DialogTitle>
            </DialogHeader>
            <p>This is a test modal. If you can see this, the modal system is working.</p>
            <p>Selected metric: {selectedMetric?.name || 'N/A'}</p>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Metric Modal */}
      <EnhancedMetricModal
        metric={selectedMetric}
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          setIsAddModalOpen(false);
          loadData(); // Refresh user metrics
          toast({
            title: 'Success',
            description: 'Metric added to your organization',
          });
        }}
      />
    </div>
  );
};
